'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Check,
  ChevronsUpDown,
  MessageCircle,
} from 'lucide-react'
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
  type CountryCode,
} from 'libphonenumber-js'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'

interface WhatsAppNumberFieldProps {
  /**
   * Canonical E.164 field submitted with FormData.
   *
   * Example:
   *   +919876543210
   */
  name?: string

  /**
   * ISO-3166 alpha-2 country field.
   *
   * Example:
   *   IN
   */
  countryName?: string

  /**
   * Name of the visible, user-facing phone Input. This value is only used so
   * FormClient can observe touched/validation state on the phone field; the API
   * relies on the canonical `name` (E.164) and `countryName` fields.
   *
   * Example:
   *   whatsappNumberInput
   */
  inputName?: string

  label?: string
  required?: boolean
  defaultCountry?: CountryCode

  /**
   * Optional initial E.164 number.
   *
   * Example:
   *   +919876543210
   */
  defaultValue?: string

  disabled?: boolean
}

interface CountryOption {
  code: CountryCode
  name: string
  callingCode: string
  flag: string
}

function countryFlag(code: CountryCode): string {
  return code
    .toUpperCase()
    .split('')
    .map((char) =>
      String.fromCodePoint(127397 + char.charCodeAt(0)),
    )
    .join('')
}

function buildCountryOptions(): CountryOption[] {
  const displayNames = new Intl.DisplayNames(['en'], {
    type: 'region',
  })

  return getCountries()
    .map((code) => ({
      code,
      name: displayNames.of(code) || code,
      callingCode: getCountryCallingCode(code),
      flag: countryFlag(code),
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

const COUNTRY_OPTIONS = buildCountryOptions()

function parseInitialValue(
  value: string | undefined,
  fallbackCountry: CountryCode,
): {
  country: CountryCode
  displayNumber: string
} {
  if (!value) {
    return {
      country: fallbackCountry,
      displayNumber: '',
    }
  }

  const parsed = parsePhoneNumberFromString(value)

  if (!parsed) {
    return {
      country: fallbackCountry,
      displayNumber: value,
    }
  }

  return {
    country: parsed.country || fallbackCountry,
    displayNumber: parsed.formatNational(),
  }
}

/**
 * Effective country for a raw value:
 *   - national input (no "+")           → the selected / default country,
 *   - resolved explicit international    → the country libphonenumber-js resolved
 *     from the number (e.g. "+61412..." → AU, "+91 98765..." → IN),
 *   - unresolved international prefix    → `undefined` (e.g. "+", "+6", "+61"),
 *     while the country is still ambiguous.
 *
 * For explicit "+" input we use a COUNTRY-AGNOSTIC AsYouType and never fall back
 * to the dropdown country, so an unresolved prefix is not reinterpreted as +91...
 */
function getEffectiveCountry(
  raw: string,
  defaultCountry: CountryCode,
): CountryCode | undefined {
  if (!raw.trimStart().startsWith('+')) return defaultCountry
  const detector = new AsYouType()
  detector.input(raw)
  return detector.getCountry()
}

/**
 * National significant digits under `country`, stripping an explicitly entered
 * calling code (only meaningful for international input). For "9123456789"
 * (no "+") the leading "91" is kept; only an explicit "+91..." is treated as a
 * calling code.
 */
function nationalDigitsFor(
  raw: string,
  country: CountryCode,
  isIntl: boolean,
): string {
  let digits = raw.replace(/\D/g, '')
  if (isIntl && isSupportedCountry(country)) {
    const cc = getCountryCallingCode(country)
    if (digits.startsWith(cc)) digits = digits.slice(cc.length)
  }
  return digits
}

/**
 * Cap national digits to the country's numbering plan:
 *   - India: at most 10 national digits (the +91 calling code is not counted),
 *   - other countries: metadata-driven; only a candidate that is definitively
 *     TOO_LONG for every permitted length has its trailing digit refused, so
 *     multi-length plans keep accepting input. Final validity is decided by
 *     isValid() and the authoritative server, not by a hardcoded max table.
 */
function capNational(national: string, country: CountryCode): string {
  if (country === 'IN') return national.slice(0, 10)
  let n = national
  while (n.length > 0 && validatePhoneNumberLength(n, country) === 'TOO_LONG') {
    n = n.slice(0, -1)
  }
  return n
}

/**
 * Country-aware normalization for typing, paste and country change (one shared
 * path). Three states:
 *   1. national input               → dropdown country is the default,
 *   2. resolved international input  → apply the resolved country's length rule,
 *   3. unresolved international prefix → preserve the natural international
 *      formatting verbatim (no dropdown fallback, no calling-code injection, no
 *      India rule) so the user can keep typing.
 */
function normalizePhoneInput(
  raw: string,
  defaultCountry: CountryCode,
): string {
  if (!raw.trim()) return ''

  if (raw.trimStart().startsWith('+')) {
    // Country-agnostic formatter: never inject the dropdown calling code.
    const formatter = new AsYouType()
    const formatted = formatter.input(raw)
    const resolved = formatter.getCountry()
    if (!resolved || !isSupportedCountry(resolved)) {
      // Unresolved prefix (e.g. "+", "+6", "+61"): preserve and keep typing.
      return formatted
    }
    const national = capNational(
      nationalDigitsFor(raw, resolved, true),
      resolved,
    )
    return new AsYouType(resolved).input(
      `+${getCountryCallingCode(resolved)}${national}`,
    )
  }

  // National input: the dropdown country is the default.
  const national = capNational(raw.replace(/\D/g, ''), defaultCountry)
  return new AsYouType(defaultCountry).input(national)
}

const WhatsAppNumberField: React.FC<WhatsAppNumberFieldProps> = ({
  name = 'whatsappNumber',
  countryName = 'whatsappCountry',
  inputName = 'whatsappNumberInput',
  label = 'WhatsApp Number',
  required = false,
  defaultCountry = 'IN',
  defaultValue,
  disabled = false,
}) => {
  const initial = useMemo(
    () => parseInitialValue(defaultValue, defaultCountry),
    [defaultCountry, defaultValue],
  )

  const [country, setCountry] = useState<CountryCode>(
    initial.country,
  )

  const [displayNumber, setDisplayNumber] = useState(
    initial.displayNumber,
  )

  const [countryOpen, setCountryOpen] = useState(false)

  const visibleInputRef = useRef<HTMLInputElement>(null)

  // React updating the visible input's value (via country reformatting or the
  // controlled displayNumber) does not always emit a native bubbling input
  // event, so dispatch one whenever either changes. This lets FormClient's
  // delegated input/change validation re-run for the phone field.
  useEffect(() => {
    const input = visibleInputRef.current
    if (!input) return

    input.dispatchEvent(
      new Event('input', {
        bubbles: true,
      }),
    )
  }, [country, displayNumber])

  const selectedCountry =
    COUNTRY_OPTIONS.find((option) => option.code === country) ??
    COUNTRY_OPTIONS.find((option) => option.code === 'IN')!

  const parsedPhone = useMemo(() => {
    const trimmed = displayNumber.trim()

    if (!trimmed) return null

    try {
      return parsePhoneNumberFromString(trimmed, country) ?? null
    } catch {
      return null
    }
  }, [country, displayNumber])

  const isValid =
    displayNumber.trim().length > 0 &&
    parsedPhone?.isValid() === true

  const canonicalNumber = (() => {
    if (!isValid || !parsedPhone) return ''
    // Defense-in-depth for India: require exactly 10 national digits (the +91
    // calling code is not counted). isValid() already enforces this today, but
    // the explicit rule protects against future metadata changes.
    if (
      parsedPhone.country === 'IN' &&
      String(parsedPhone.nationalNumber).replace(/\D/g, '').length !== 10
    ) {
      return ''
    }
    return parsedPhone.number
  })()

  const handleCountryChange = (nextCountry: CountryCode) => {
    setCountry(nextCountry)
    setCountryOpen(false)

    if (displayNumber.trim()) {
      // Reinterpret the current national digits under the newly selected country.
      // If the current value was an explicit resolved international number, drop
      // its calling code so it is not treated as part of the new national number.
      const isIntl = displayNumber.trimStart().startsWith('+')
      const current = getEffectiveCountry(displayNumber, country)
      const national =
        isIntl && current
          ? nationalDigitsFor(displayNumber, current, true)
          : displayNumber.replace(/\D/g, '')
      setDisplayNumber(normalizePhoneInput(national, nextCountry))
    }
  }

  const handleNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDisplayNumber(normalizePhoneInput(event.target.value, country))
  }

  return (
    <div className="grid gap-1.5 sm:gap-2">
      <Label htmlFor={`${name}-visible`} className="text-sm">
        {label}

        {required && (
          <span
            className="text-red-500"
            aria-hidden="true"
          >
            {' '}
            *
          </span>
        )}
      </Label>

      {/*
       * Canonical values submitted with the application.
       *
       * We deliberately do not submit the formatted visual input.
       * The backend receives:
       *
       * whatsappNumber  = +919876543210
       * whatsappCountry = IN
       */}
      <input
        type="hidden"
        name={name}
        value={canonicalNumber}
        readOnly
        disabled={disabled}
      />

      <input
        type="hidden"
        name={countryName}
        value={country}
        readOnly
        disabled={disabled}
      />

      <div className="flex w-full">
        <Popover
          open={countryOpen}
          onOpenChange={(open) => {
            if (!disabled) setCountryOpen(open)
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={`Country code: ${selectedCountry.name} +${selectedCountry.callingCode}`}
              aria-expanded={countryOpen}
              className={cn(
                'flex h-10 shrink-0 items-center gap-1.5',
                'rounded-l-md border border-r-0 border-input',
                'bg-background px-3 text-sm shadow-sm',
                'transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                'focus-visible:z-10 focus-visible:outline-none',
                'focus-visible:ring-2 focus-visible:ring-ring',
                'focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              <span
                className="text-base leading-none"
                aria-hidden="true"
              >
                {selectedCountry.flag}
              </span>

              <span className="font-medium">
                +{selectedCountry.callingCode}
              </span>

              <ChevronsUpDown
                className="h-3.5 w-3.5 opacity-50"
                aria-hidden="true"
              />
            </button>
          </PopoverTrigger>

          <PopoverContent
            align="start"
            className="w-[min(22rem,calc(100vw-2rem))] p-0"
          >
            <Command>
              <CommandInput placeholder="Search country or calling code…" />

              <CommandList>
                <CommandEmpty>
                  No country found.
                </CommandEmpty>

                <CommandGroup>
                  {COUNTRY_OPTIONS.map((option) => (
                    <CommandItem
                      key={option.code}
                      value={[
                        option.name,
                        option.code,
                        `+${option.callingCode}`,
                      ].join(' ')}
                      onSelect={() =>
                        handleCountryChange(option.code)
                      }
                      className="cursor-pointer"
                    >
                      <span
                        className="w-6 shrink-0 text-base"
                        aria-hidden="true"
                      >
                        {option.flag}
                      </span>

                      <span className="min-w-0 flex-1 truncate">
                        {option.name}
                      </span>

                      <span className="shrink-0 text-muted-foreground">
                        +{option.callingCode}
                      </span>

                      <Check
                        className={cn(
                          'ml-1 h-4 w-4 shrink-0',
                          country === option.code
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                        aria-hidden="true"
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <div className="relative min-w-0 flex-1">
          <MessageCircle
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />

          {/*
           * FormClient is the single owner of form validation messaging. It reads
           * the canonical hidden `whatsappNumber` value and sets aria-invalid, the
           * error border and the inline error on this visible named input. This
           * component only owns country selection, formatting and the canonical
           * values.
           */}
          <Input
            ref={visibleInputRef}
            id={`${name}-visible`}
            name={inputName}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={displayNumber}
            onChange={handleNumberChange}
            disabled={disabled}
            aria-describedby={`${inputName}-hint`}
            placeholder="98765 43210"
            className={cn('h-10 rounded-l-none pl-9')}
          />
        </div>
      </div>

      {/* Static helper. FormClient hides this (via data-field-hint) while an error
          for this field is being displayed. */}
      <p
        id={`${inputName}-hint`}
        data-field-hint={inputName}
        className="text-[11px] text-muted-foreground sm:text-xs"
      >
        Select your country and enter the number you use on WhatsApp.
      </p>
    </div>
  )
}

export default WhatsAppNumberField
